import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Controller, get } from 'react-hook-form';

import { Button } from 'primereact/button';
import { Chips } from 'primereact/chips';
import { Card } from 'primereact/card';
import { AutoComplete } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import { Link } from 'react-router-dom';
import { useToggle } from 'hooks/useToggle';
import { useDebounce } from 'hooks/useDebounce';

import { useToast } from 'context/ToastContext';
import AppConstants from 'constants/index';
import { toastConstant } from 'constants/toastmessage';
import { createVenue } from 'services/Settings';
import { createContact } from 'services/contact';
import { useContactList } from 'hooks/useContactList';
import { updateCaseContactV2 } from 'services/CaseServices';
import { useGlobalTagList } from 'hooks/useGlobalTags';
import { useVenueList } from 'hooks/useVenueList';
import { useOfficeLocation } from 'hooks/useOfficeLocation';
import { useCaseMatter } from 'modules/cases/hooks/useCaseMatter';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { usePracticeAreaList } from 'hooks/usePracticeAreaList';
import { formatDateWithoutTimezone } from 'utils/utils';
import { parseDOB } from 'modules/lead/helpers/helpers';
import Cookies from 'universal-cookie';

import AsyncPaginate from 'components/AsyncPaginate/AsyncPaginate';
import { getOfficeLocationsV2 } from 'services/Settings';
import { listGlobalTags } from 'services/Settings';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';

const Summary = ({
  control,
  userDetails,
  caseDetails,
  watch,
  setValue,
  fetchCaseStage,
  caseStageList,
  loadCourtContacts,
  isLeadSpecific,
  handleSubmit,
  getCaseDetails,
}) => {
  const practiceAreaValue = caseDetails?.case_description?._case_practice_area_;
  const practiceAreaId = caseDetails?.case_description?.case_practice_area;
  let caseMatterValue = caseDetails?.case_description?._case_matter_ || null;
  let caseMatterId = caseDetails?.case_description?.case_matter || null;
  const venueValue = caseDetails?.case_description?._venue_;
  const venueId = caseDetails?.case_description?.venue;
  const officeLocationValue = caseDetails?.case_description?._office_location_;
  const officeLocationId = caseDetails?.case_description?.office_location;
  const courtId = caseDetails?.case_description?.court;
  const courtName = caseDetails?.case_description?._court_;

  //----------- custom hook ----------
  const { addToast } = useToast();
  const { practiceAreaList } = usePracticeAreaList(false, 15, 'all', false, practiceAreaValue);
  const { searchContactsWithFullObjects, loadInitialContacts } = useContactList({
    isCase: !isLeadSpecific,
    isLeads: isLeadSpecific,
  });

  const { modifiedOfficeLocationList } = useOfficeLocation({
    isCase: !isLeadSpecific,
    isLeads: isLeadSpecific,
  });
  const cookie = new Cookies();

  // -----------------useState ------------------------
  const [isAddCourtLoading, setIsAddCourtLoading] = useState(false);
  const [toggleCourt, setToggleCourt] = useState(false);
  const [currentPracticeArea, setCurrentPracticeArea] = useState(caseDetails?.case_description?.case_practice_area);
  const { caseMatterList } = useCaseMatter(
    currentPracticeArea,
    {
      isCase: !isLeadSpecific,
      isLeads: isLeadSpecific,
    },
    false,
    15
    // caseMatterValue
  );
  const { formattedGlobalTag, globalTagToken, loadGlobalList, setGlobalTagToken } = useGlobalTagList(currentPracticeArea, {}, false, 10);
  const { loadVenue } = useVenueList(
    '',
    currentPracticeArea,
    {
      isCase: !isLeadSpecific,
      isLeads: isLeadSpecific,
    },
    false,
    15
    // venueValue,
    // true
  );

  const [toggleVenue, setToggleVenue] = useState(false);
  const [isAddVenueLoading, setIsAddVenueLoading] = useState(false);

  const [showGlobalTags, setShowGlobalTags] = useState(false);

  const [age, setAge] = useState();
  const clientDetails = caseDetails?.client_details?.[0];

  const [isFullNamedropdown, setToggleFullName] = useToggle(false);

  // Client AutoComplete states
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [clientInputValue, setClientInputValue] = useState('');

  // Court AutoComplete states
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [courtSuggestions, setCourtSuggestions] = useState([]);
  const [courtInputValue, setCourtInputValue] = useState('');

  // Search query states for debouncing
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [courtSearchQuery, setCourtSearchQuery] = useState('');

  // Debounced search queries
  const debouncedClientQuery = useDebounce(clientSearchQuery, 300);
  const debouncedCourtQuery = useDebounce(courtSearchQuery, 300);

  const [practiceAreaListOptions, setPracticeAreaListOptions] = useState([]);
  const [globalTagsList, setGlobalTagsList] = useState([]);

  const [caseMatterListOptions, setCaseMatterListOptions] = useState([]);
  const [venueListOptions, setVenueListOptions] = useState([]);
  const [courtListOptions, setCourtListOptions] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(venueId);
  const [newVenueName, setNewVenueName] = useState('');
  const [newCourtName, setNewCourtName] = useState('');
  // ---------------custom function----------------------------

  // Get the display value for Client AutoComplete
  const getClientAutoCompleteValue = () => {
    if (clientInputValue) {
      return clientInputValue;
    }
    if (selectedContactId && selectedContact && !clientInputValue) {
      return `${selectedContact?.first_name || ''} ${selectedContact?.middle_name || ''} ${selectedContact?.last_name || ''}`
        .replace(/\s+/g, ' ')
        .trim();
    }
    return '';
  };

  const searchClient = useCallback(async (event) => {
    const query = event.query;

    setClientSearchQuery(query);
  }, []);

  // Search function for Court AutoComplete
  const searchCourt = useCallback(async (event) => {
    const query = event.query;

    setCourtSearchQuery(query);
  }, []);

  const onSelectClient = (e) => {
    if (e.value && e.value.value) {
      setSelectedContactId(e.value.value);
      setSelectedContact(e.value.client);
      setClientInputValue(e.value.label);
    }
  };

  const onSelectCourt = (e) => {
    if (e.value && e.value.value) {
      setSelectedCourtId(e.value.value);
      setSelectedCourt({ title: e.value.label, contact_id: e.value.value });
      setCourtInputValue(e.value.label);
      setValue('case_description.court', e.value.value);
    }
  };

  const getCourtAutoCompleteValue = () => {
    if (courtInputValue) {
      return courtInputValue;
    }
    if (selectedCourtId && selectedCourt) {
      return selectedCourt?.title || '';
    }
    return '';
  };

  const handleAddCaseCourt = () => {
    if (watch('case_description.court') && toggleCourt) {
      setIsAddCourtLoading(true);
      let data = {};
      data = {
        phone: '',
        title: watch('case_description.court'),
        contact_type: 'location',
        is_court_location: true,
        email: '',
        country_code: '+1',
      };
      createContact(data)
        .then((response) => {
          setToggleCourt(false);
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CASE_COURT_CREATED);
          setIsAddCourtLoading(false);
          const newCourt = response?.data;
          if (newCourt) {
            const courtSuggestion = {
              label: newCourt.title || data.title,
              value: newCourt.contact_id,
            };
            setCourtSuggestions((prev) => {
              const exists = prev.some((suggestion) => suggestion.value === newCourt.contact_id);
              if (!exists) {
                return [courtSuggestion, ...prev];
              }
              return prev;
            });
            setSelectedCourtId(newCourt.contact_id);
            setSelectedCourt({ title: newCourt.title || data.title, contact_id: newCourt.contact_id });
            setCourtInputValue(newCourt.title || data.title);
          }

          loadCourtContacts();
          setValue('case_description.court', response?.data?.contact_id);
          setNewCourtName(response?.data?.title);
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            addToast(false, toastConstant.toasterType.WARN, toastConstant.api.FAILED, error.response.data);
          }
          setIsAddCourtLoading(false);
        });
    } else {
      addToast(false, toastConstant.toasterType.INFO, toastConstant.api.FAILED, toastConstant.message.CASE_COURT_NAME_VALIDATION);
    }
  };

  const handleAddVenue = () => {
    if (watch('case_description.venue') && toggleVenue) {
      setIsAddVenueLoading(true);
      let data = {};
      data = {
        venue_name: watch('case_description.venue'),
      };
      createVenue(data)
        .then((response) => {
          setToggleVenue(false);
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.VENUE_CREATED);
          setIsAddVenueLoading(false);
          loadVenue();
          setValue('case_description.venue', response?.data?.venue_id);
          setNewVenueName(response?.data?.venue_name);
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            addToast(false, toastConstant.toasterType.WARN, toastConstant.api.FAILED, error.response.data);
          }
          setIsAddVenueLoading(false);
        });
    } else {
      addToast(false, toastConstant.toasterType.INFO, toastConstant.api.FAILED, toastConstant.message.VENUE_NAME_VALIDATION);
    }
  };

  const chipTemplate = (e) => {
    if (e) {
      try {
        let formattedTagData = e?.replaceAll('#', '');
        return <div>{'#' + formattedTagData}</div>;
      } catch (error) {
        return <div>{e}</div>;
      }
    }
  };

  const assignToItemTemplate = (option) => {
    if (option?.status === 'ARCHIVED') {
      return `${option?.practice_area_name} (Archived)`;
    }
    return option?.practice_area_name;
  };

  const practiceAreaOptions = useMemo(
    () =>
      practiceAreaList.filter(
        (option) =>
          caseDetails?.case_description?.case_practice_area?.trim() === option?.practice_area_name?.trim() || option?.status === 'ACTIVE'
      ),
    [practiceAreaList, caseDetails?.case_description?.case_practice_area]
  );

  useEffect(() => {
    if (clientDetails?.dob) {
      ageCalculation(clientDetails?.dob);
    } else {
      setAge('');
    }
  }, [caseDetails, clientDetails?.dob]);

  useEffect(() => {
    if (caseDetails?.case_description?.case_practice_area) {
      setCurrentPracticeArea(caseDetails?.case_description?.case_practice_area);
    }
  }, [caseDetails?.case_description?.case_practice_area]);

  // Convert string IDs to objects for Office Location after form reset
  useEffect(() => {
    if (!caseDetails?.case_description?.office_location || !modifiedOfficeLocationList?.length) return;

    const officeLocationValue = watch('case_description.office_location');
    if (typeof officeLocationValue === 'string' && officeLocationValue) {
      const foundOption = modifiedOfficeLocationList?.find((opt) => opt.office_location_id === officeLocationValue);
      if (foundOption) {
        setValue('case_description.office_location', foundOption, { shouldDirty: false });
      } else if (officeLocationId && officeLocationValue) {
        // Fallback to using the display value and ID from caseDetails
        setValue(
          'case_description.office_location',
          {
            office_location_id: officeLocationId,
            office_name: officeLocationValue,
            office_nameLabel: officeLocationValue,
            label: officeLocationValue,
            value: officeLocationId,
          },
          { shouldDirty: false }
        );
      }
    }
  }, [modifiedOfficeLocationList, caseDetails?.case_description?.office_location, setValue, officeLocationId, officeLocationValue, watch]);

  const ageCalculation = (dob) => {
    if (!dob) return '';
    const DOB = parseDOB(dob);
    if (!DOB) return '';
    const today = new Date();
    const age = today.getFullYear() - DOB.getFullYear();
    setAge(age);
  };

  const handleClientInformationSubmit = async (data, isRetain = false) => {
    const contact = selectedContact;

    if (!isLeadSpecific) {
      await updateCaseContactV2(caseDetails?.case_id, { ...contact, role: undefined }, clientDetails?.sk);
      await getCaseDetails();
      setToggleFullName(false);
    }
  };

  useEffect(() => {
    const currentClient = caseDetails?.client_details?.[0];

    if (currentClient) {
      const currentClientOption = {
        label: `${currentClient?.first_name || ''} ${currentClient?.middle_name || ''} ${currentClient?.last_name || ''}`
          .replace(/\s+/g, ' ')
          .trim(),
        value: currentClient?.contact_id,
        client: currentClient,
      };
      setClientSuggestions((prev) => {
        const exists = prev.some((suggestion) => suggestion.value === currentClient?.contact_id);
        if (!exists) {
          return [currentClientOption, ...prev];
        }
        return prev;
      });
    }
  }, [caseDetails?.client_details]);

  useEffect(() => {
    const performClientSearch = async () => {
      if (debouncedClientQuery && debouncedClientQuery.length > 0) {
        const results = await searchContactsWithFullObjects(debouncedClientQuery);

        let suggestions = [...results];

        const currentClient = caseDetails?.client_details?.[0];
        if (currentClient) {
          const currentClientName = `${currentClient?.first_name || ''} ${currentClient?.middle_name || ''} ${
            currentClient?.last_name || ''
          }`
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
          const searchQuery = debouncedClientQuery.toLowerCase();

          if (currentClientName.includes(searchQuery)) {
            const currentClientSuggestion = {
              label: `${currentClient?.first_name || ''} ${currentClient?.middle_name || ''} ${currentClient?.last_name || ''}`
                .replace(/\s+/g, ' ')
                .trim(),
              value: currentClient?.contact_id,
              client: currentClient, // Full contact object
            };

            const isAlreadyInResults = results.some((result) => result.value === currentClient?.contact_id);
            if (!isAlreadyInResults) {
              suggestions.unshift(currentClientSuggestion);
            }
          }
        }

        setClientSuggestions(suggestions);
      } else {
        setClientSuggestions([]);
      }
    };

    performClientSearch();
  }, [debouncedClientQuery, searchContactsWithFullObjects, caseDetails?.client_details]);

  // Set initial client values
  useEffect(() => {
    if (caseDetails?.client_details?.[0]) {
      const client = caseDetails.client_details[0];
      setSelectedContactId(client.contact_id);
      setSelectedContact(client);
      setClientInputValue(
        `${client?.first_name || ''} ${client?.middle_name || ''} ${client?.last_name || ''}`.replace(/\s+/g, ' ').trim()
      );
    }
  }, [caseDetails]);

  // Effect to add current court to suggestions (no initial API call needed)
  useEffect(() => {
    // Only add current court from caseDetails to suggestions if we have the data
    const currentCourtId = caseDetails?.case_description?.court;
    const currentCourtName = caseDetails?.case_description?._court_;

    if (currentCourtId && currentCourtName) {
      const currentCourtOption = {
        label: currentCourtName,
        value: currentCourtId,
      };

      // Add current court to suggestions if not already present
      setCourtSuggestions((prev) => {
        const exists = prev.some((suggestion) => suggestion.value === currentCourtId);
        if (!exists) {
          return [currentCourtOption, ...prev];
        }
        return prev;
      });
    }
  }, [caseDetails?.case_description?.court, caseDetails?.case_description?._court_]);

  // Set initial court values
  useEffect(() => {
    // First check if we have court data from case_description (court ID and _court_ name)
    const courtId = caseDetails?.case_description?.court;
    const courtName = caseDetails?.case_description?._court_;

    if (courtId && courtName) {
      // Use the court data from case_description
      setSelectedCourtId(courtId);
      setSelectedCourt({ title: courtName, contact_id: courtId });
      setCourtInputValue(courtName);
    } else if (caseDetails?.court_details?.[0]) {
      // Fallback to court_details if available
      const court = caseDetails.court_details[0];
      setSelectedCourtId(court.contact_id);
      setSelectedCourt(court);
      setCourtInputValue(court?.title || '');
    }
  }, [caseDetails]);

  const loadCaseMatterOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;
    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;
    const data = {
      practice_area_id: currentPracticeArea ? [currentPracticeArea] : [],
    };

    const options = await getGlobalDropdown(
      '',
      'case_matter',
      inputValue,
      caseDetails?.case_id,
      isLeadSpecific,
      page,
      pageLimit,
      '',
      '',
      data
    );

    setCaseMatterListOptions((prev) => [...prev, ...(options?.data?.data || [])]);

    return {
      options: options?.data?.data || [],
      hasMore: options?.has_next || options?.data?.has_next,
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
    };
  };

  const loadPracticeAreaOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;
    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const options = await getGlobalDropdown('', 'practice_area', inputValue, caseDetails?.case_id, isLeadSpecific, page, pageLimit);

    setPracticeAreaListOptions((prev) => [...prev, ...(options?.data?.data || [])]);
    return {
      options: options?.data?.data || [],
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
      hasMore: options?.data?.has_next,
    };
  };

  const loadVenueOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;
    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;
    const data = {
      practice_area_id: currentPracticeArea ? [currentPracticeArea] : [],
    };

    const options = await getGlobalDropdown('', 'venue', inputValue, caseDetails?.case_id, isLeadSpecific, page, pageLimit, '', '', data);

    setVenueListOptions((prev) => [...prev, ...(options?.data?.data || [])]);

    return {
      options: options?.data?.data || [],
      hasMore: options?.data?.has_next,
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
    };
  };

  const loadOfficeLocationOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;

    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const pageToFetch = currentPage + 1;

    const options = await getOfficeLocationsV2({
      keyword: inputValue,
      page: pageToFetch,
      limit: pageLimit,
      sort_by: 'time',
      sort_order: 'desc',
      is_dropdown: true,
      include_contacts: false,
    });

    const responseOptions =
      options?.data?.data?.map((item) => ({
        label: item?.is_primary_office === true ? `${item?.office_name} (Primary Office)` : item?.office_name,
        value: item?.office_location_id,
      })) || [];

    return {
      options: responseOptions || [],
      hasMore: options?.data?.has_next ? true : false,
      additional: {
        pageLimit: pageLimit,
        page: pageToFetch, // Track the current page for next load
      },
    };
  };

  const loadCourtOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;
    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const options = await getGlobalDropdown(
      '',
      'contact',
      inputValue,
      caseDetails?.case_id,
      isLeadSpecific,
      page,
      pageLimit,
      '',
      '',
      '',
      false,
      'ACTIVE',
      'location',
      true
    );

    setCourtListOptions((prev) => [...prev, ...(options?.data?.data || [])]);

    return {
      options: options?.data?.data || [],
      hasMore: options?.data?.has_next,
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
    };
  };

  const loadMoreGlobalTags = async () => {
    const nextToken = globalTagToken || '';
    const globalTags = await listGlobalTags(10, nextToken, '', '', currentPracticeArea);
    const formattedGlobalTags = globalTags?.data?.global_tag?.map((item) => ({ name: item?.tag, value: item?.tag }));

    setGlobalTagToken(globalTags?.data?.next_token);
    setGlobalTagsList((prev) => [...prev, ...formattedGlobalTags]);
  };

  useEffect(() => {
    if (formattedGlobalTag?.length > 0) {
      setGlobalTagsList(formattedGlobalTag);
    }
  }, [formattedGlobalTag]);

  // ------------------------------------------------ here start jsx code --------------------------------

  return (
    <>
      <div className="caption-bold">{isLeadSpecific ? 'Potential Case Details' : 'Summary'}</div>
      {/* summary---------------------------------------------------------------  */}
      <Card className="card-bg F-size14">
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Case Name</div>
          <div className="col-md-8 col-12 d-flex align-items-center edit-field">
            <Controller
              name="case_description.case_name"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="text"
                  className="input-shadow w-100"
                  placeholder="Enter Case Name"
                  {...field}
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                />
              )}
            />
          </div>
        </div>
        {caseDetails?.case_description?._case_practice_area_ !== 'Personal Injury' && !isLeadSpecific && (
          <>
            <div className="row align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 text-muted p-medium">Client's Full Name</div>
              <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                {isFullNamedropdown ? (
                  <>
                    <Controller
                      name="client_details.full_name"
                      control={control}
                      render={({ field }) => (
                        <AutoComplete
                          placeholder="Search and Select Client"
                          style={{ width: '100%' }}
                          inputStyle={{ width: '100%' }}
                          value={getClientAutoCompleteValue()}
                          suggestions={clientSuggestions}
                          onSelect={(e) => onSelectClient(e)}
                          completeMethod={searchClient}
                          field="label"
                          onFocus={async () => {
                            // Load initial suggestions when focused (like Defendant component)
                            if (clientSuggestions.length === 0) {
                              try {
                                const suggestions = await loadInitialContacts();
                                setClientSuggestions(suggestions);
                              } catch (error) {
                                console.log(error);
                              }
                            }
                          }}
                          onChange={(e) => {
                            field.onChange(e.value);
                            // Update input value for typing
                            setClientInputValue(e.value);
                            // Update search query for debouncing
                            setClientSearchQuery(e.value || '');
                            // Clear selection if user types manually and it's different from selected contact
                            const currentContactName = selectedContact
                              ? `${selectedContact?.first_name || ''} ${selectedContact?.middle_name || ''} ${
                                  selectedContact?.last_name || ''
                                }`
                                  .replace(/\s+/g, ' ')
                                  .trim()
                              : '';
                            if (e.value && typeof e.value === 'string' && e.value !== currentContactName) {
                              setSelectedContactId(null);
                              setSelectedContact(null);
                            } else if (!e.value) {
                              // Clear everything when field is empty
                              setSelectedContactId(null);
                              setSelectedContact(null);
                            }
                          }}
                          className="flex-grow-1 input-shadow"
                        />
                      )}
                    />

                    <Button
                      icon="pi pi-times"
                      className="p-button-secondary outline mx-2"
                      type="button"
                      onClick={() => {
                        setToggleFullName();
                        setSelectedContactId(clientDetails?.contact_id);
                      }}
                    />
                    <Button
                      icon="pi pi-check"
                      className="p-button-secondary outline"
                      type="button"
                      onClick={() => handleSubmit((data) => handleClientInformationSubmit(data, false))()}
                    />
                  </>
                ) : (
                  <>
                    <Link to={`/contacts/${caseDetails?.client_details?.[0]?.contact_id || ''}`} className="text-decoration-none">
                      <div className="title-link-wrap cursor-pointer">
                        <h5 className="title-text mb-0 redirection-link-no-text-transform">
                          {`${clientDetails?.first_name} ${clientDetails?.middle_name || ''} ${clientDetails?.last_name}`
                            .replace(/\s+/g, ' ')
                            .trim()}
                        </h5>
                      </div>
                    </Link>
                    <div className="col-1 col-md-1 px-2 d-flex align-items-center ms-1 w-auto">
                      <span className="pointer">
                        <i
                          className="icon-edit"
                          onClick={() => {
                            setToggleFullName(true);
                          }}
                        ></i>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="row align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 text-muted p-medium">Client's DOB</div>
              <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                <span>{clientDetails?.dob ? formatDateWithoutTimezone(clientDetails?.dob) : '-'}</span>
              </div>
            </div>
            <div className="row align-items-center py-2 flex-wrap">
              <div className="col-md-4 col-12 text-muted p-medium">Client's Age</div>
              <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                <span>{age}</span>
              </div>
            </div>
          </>
        )}

        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Practice Area</div>
          <div className="col-md-8 col-12 d-flex align-items-center edit-field">
            {/* <Controller
              name="case_description.case_practice_area"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  options={practiceAreaOptions}
                  onChange={(e) => {
                    const newValue = e.value;
                    const oldValue = currentPracticeArea;

                    if (newValue !== oldValue) {
                      confirmDialog({
                        message:
                          'Changing the practice area will impact associated billing codes and other dependent configurations. Do you want to continue.',
                        header: 'Change Practice Area',
                        icon: 'pi pi-exclamation-triangle',
                        accept: () => {
                          field.onChange(newValue);
                          setCurrentPracticeArea(newValue);
                          fetchCaseStage(newValue);
                          let dat = caseStageList.find((val) => val.stage_name === 'Open');
                          if (dat?.stage_name) {
                            setValue('case_description.case_stage', dat?.stage_name);
                          }
                        },
                        reject: () => {
                          field.onChange(oldValue);
                        },
                        closable: false,
                      });
                    }
                  }}
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                  optionLabel="practice_area_name"
                  optionValue="practice_area_id"
                  placeholder="Select Practice Area"
                  className="input-shadow w-100"
                  itemTemplate={assignToItemTemplate}
                  filter
                />
              )}
            /> */}
            <Controller
              name="case_description.case_practice_area"
              control={control}
              render={({ field }) => {
                let selectedOption = practiceAreaListOptions?.find((opt) => opt.practice_area_id === field?.value) || null;
                if (!selectedOption && watch('case_description.case_practice_area')) {
                  selectedOption = (practiceAreaValue && practiceAreaId && { label: practiceAreaValue, value: practiceAreaId }) || null;
                }
                return (
                  <AsyncPaginate
                    name="case_practice_area"
                    value={selectedOption}
                    onChange={(selectedOption) => {
                      let newValue = selectedOption;
                      const oldValue =
                        typeof currentPracticeArea === 'string'
                          ? currentPracticeArea
                          : currentPracticeArea?.practice_area_id || currentPracticeArea?.value;
                      newValue = typeof newValue === 'string' ? newValue : newValue?.practice_area_id || newValue?.value;

                      if (newValue !== oldValue) {
                        confirmDialog({
                          message:
                            'Changing the practice area will impact associated billing codes and other dependent configurations. Do you want to continue.',
                          header: 'Change Practice Area',
                          icon: 'pi pi-exclamation-triangle',

                          accept: () => {
                            field.onChange(newValue);
                            setCurrentPracticeArea(newValue);
                            fetchCaseStage(newValue);
                            let dat = caseStageList?.find((val) => val?.stage_name === 'Open');
                            if (dat?.stage_name) {
                              setValue('case_description.case_stage', dat?.stage_name);
                            }
                            // After PA changes , CM will be empty
                            setValue('case_description.case_matter', '');
                            setValue('case_description.venue', '');
                          },

                          reject: () => {
                            field.onChange(field?.value);
                          },

                          closable: false,
                        });
                      }
                    }}
                    loadOptions={loadPracticeAreaOptions}
                    getOptionLabel={(option) => option?.label || option?.practice_area_name}
                    getOptionValue={(option) => option?.value || option?.practice_area_id}
                    additional={{
                      pageLimit: 15,
                      page: 0,
                    }}
                    debounceTimeout={300}
                    placeholder="Select Practice Area"
                    disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                    className="flex-grow-1 w-75 border-0 input-shadow"
                    styles={userDetails?.firmDetails?.appearance_preference}
                  />
                );
              }}
            />
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Case Matter</div>
          <div className="col-md-8 col-12 d-flex align-items-center edit-field">
            {/* <Controller
              name="case_description.case_matter"
              control={control}
              render={({ field }) => (
                <Dropdown
                  name="case_matter"
                  value={field.value}
                  onChange={(event) => field.onChange(event.value)}
                  options={caseMatterList}
                  optionLabel="matter_name"
                  optionValue="case_matter_id"
                  placeholder="Select Case Matter"
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                  className="flex-grow-1 w-75 border-0 input-shadow"
                />
              )}
            /> */}
            <Controller
              name="case_description.case_matter"
              control={control}
              render={({ field }) => {
                let selectedOption = caseMatterListOptions?.find((opt) => opt?.case_matter_id === field?.value) || null;
                if (!selectedOption && watch('case_description.case_matter')) {
                  selectedOption = (caseMatterValue && caseMatterId && { label: caseMatterValue, value: caseMatterId }) || null;
                }
                return (
                  <AsyncPaginate
                    name="case_matter"
                    value={selectedOption}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption?.case_matter_id);
                    }}
                    loadOptions={loadCaseMatterOptions}
                    getOptionLabel={(option) => option?.label || option?.matter_name}
                    getOptionValue={(option) => option?.value || option?.case_matter_id}
                    additional={{
                      pageLimit: 15,
                      page: 0,
                    }}
                    debounceTimeout={300}
                    placeholder="Select Case Matter"
                    className="flex-grow-1 w-75 border-0 input-shadow"
                    cacheUniqs={[watch('case_description.case_practice_area')]}
                    styles={userDetails?.firmDetails?.appearance_preference}
                  />
                );
              }}
            />
          </div>
        </div>

        <div className="row align-items-start py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Description</div>
          <div className="col-md-8 col-12 edit-field d-flex align-items-center">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  type="textarea"
                  rows={16}
                  cols={30}
                  value={field.value}
                  placeholder="Enter Description"
                  onChange={(e) => field.onChange(e.target.value)}
                  className="input-shadow w-100"
                  disabled={userDetails?.getPermission('cases') !== AppConstants.permissions.WRITE}
                />
              )}
            />
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 p-medium">Office Location</div>
          <div className="col-md-8 col-12">
            <Controller
              name="case_description.office_location"
              control={control}
              render={({ field }) => {
                const selectedOption =
                  typeof field.value === 'string'
                    ? modifiedOfficeLocationList?.find((opt) => opt.office_location_id === field.value) ||
                      (officeLocationValue ? { label: officeLocationValue, value: officeLocationId } : null)
                    : field.value;
                // <Dropdown
                //   placeholder="Select Office Location"
                //   value={field.value}
                //   options={modifiedOfficeLocationList}
                //   onChange={(e) => {
                //     field.onChange(e.value);
                //   }}
                //   optionValue="office_location_id"
                //   optionLabel="office_nameLabel"
                //   className="input-shadow border-0 w-100"
                //   filter
                // />
                return (
                  <AsyncPaginate
                    value={selectedOption}
                    onChange={(selectedOption) => {
                      // Ensure we always set the full object, not just the value
                      if (selectedOption) {
                        field.onChange(selectedOption);
                      } else {
                        field.onChange(null);
                      }
                    }}
                    loadOptions={loadOfficeLocationOptions}
                    getOptionLabel={(option) =>
                      option?.office_nameLabel ? option?.office_nameLabel : option?.label || option?.office_name
                    }
                    getOptionValue={(option) => option?.value || option?.office_location_id}
                    additional={{
                      pageLimit: 15,
                      page: 0, // Start at page 0, will fetch page 1 first
                    }}
                    debounceTimeout={300}
                    placeholder="Select Office Location"
                    className="input-shadow border-0 w-100"
                    styles={userDetails?.firmDetails?.appearance_preference}
                  />
                );
              }}
            />
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 p-medium">
            <label className="p-medium">Court</label>
          </div>
          <div className="d-flex col-md-8 col-12">
            {toggleCourt ? (
              <div className="p-inputgroup align-items-center">
                <Controller
                  name="case_description.court"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      value={field.value}
                      placeholder="Enter New Court"
                      onChange={(e) => field.onChange(e.target.value.trimStart())}
                      className="input-shadow w-100"
                    />
                  )}
                />
                <Button
                  icon="pi pi-check"
                  className="p-button-secondary mx-2"
                  type="button"
                  onClick={handleAddCaseCourt}
                  loading={isAddCourtLoading}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary outline"
                  type="button"
                  onClick={() => {
                    setToggleCourt(false);
                    setValue('case_description.court', selectedCourt ? selectedCourt?.contact_id : watch('case_description.court'));
                  }}
                  disabled={isAddCourtLoading}
                />
              </div>
            ) : (
              <>
                <Controller
                  name="case_description.court"
                  control={control}
                  render={({ field }) => {
                    // <AutoComplete
                    //   placeholder="Search and Select Court"
                    //   style={{ width: '100%' }}
                    //   inputStyle={{ width: '100%' }}
                    //   value={getCourtAutoCompleteValue()}
                    //   suggestions={courtSuggestions}
                    //   onSelect={(e) => onSelectCourt(e)}
                    //   completeMethod={searchCourt}
                    //   field="label"
                    //   onFocus={async () => {
                    //     // Load initial suggestions when focused (like Defendant component)
                    //     if (courtSuggestions.length === 0) {
                    //       try {
                    //         const suggestions = await searchCourtLocationsWithFullObjects('');
                    //         setCourtSuggestions(suggestions);
                    //       } catch (error) {
                    //         console.log(error);
                    //       }
                    //     }
                    //   }}
                    //   onChange={(e) => {
                    //     // Update search query for debouncing
                    //     setCourtSearchQuery(e.value || '');
                    //     // Only update the form field when user is typing manually
                    //     if (e.value && typeof e.value === 'string') {
                    //       field.onChange(e.value);
                    //       setCourtInputValue(e.value);
                    //       // Clear selection if user types manually
                    //       setSelectedCourtId(null);
                    //       setSelectedCourt(null);
                    //     } else if (!e.value) {
                    //       // Clear everything when field is empty
                    //       field.onChange('');
                    //       setCourtInputValue('');
                    //       setSelectedCourtId(null);
                    //       setSelectedCourt(null);
                    //     }
                    //   }}
                    //   className="input-shadow w-100"
                    // />
                    let selectedOption = courtListOptions?.find((opt) => opt?.contact_id === field?.value) || null;
                    if (newCourtName) {
                      selectedOption = { label: newCourtName, value: watch('case_description.court') };
                    } else if (!selectedOption) {
                      selectedOption = (courtName && courtId && { label: courtName, value: courtId }) || null;
                    }
                    return (
                      <AsyncPaginate
                        value={selectedOption}
                        onChange={(selectedOption) => {
                          if (newCourtName) {
                            setNewCourtName('');
                          }
                          field.onChange(selectedOption?.value || selectedOption?.contact_id);
                          setSelectedCourt({ title: selectedOption?.title, contact_id: selectedOption?.contact_id });
                        }}
                        loadOptions={loadCourtOptions}
                        getOptionLabel={(option) => option?.label || option?.court_name || option?.title}
                        getOptionValue={(option) => option?.value || option?.court_id || option?.contact_id}
                        additional={{
                          pageLimit: 15,
                          page: 0,
                        }}
                        debounceTimeout={300}
                        placeholder="Select Court"
                        className="input-shadow w-100"
                        styles={userDetails?.firmDetails?.appearance_preference}
                      />
                    );
                  }}
                />
              </>
            )}
            {userDetails?.permissions?.settings?.add_court_location && (
              <div className="col-1 col-md-1 px-1 d-flex align-items-center ms-1 w-auto">
                <span className="pointer">
                  <i
                    className="fas fa-plus-circle add-court"
                    onClick={() => {
                      setToggleCourt(true);
                      setValue('case_description.court', '');
                    }}
                  ></i>
                  <Tooltip content="Add Court" position="top" target=".add-court" showDelay={500} />
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 p-medium">
            <label className="p-medium">Venue</label>
          </div>
          <div className="d-flex col-md-8 col-12">
            {toggleVenue ? (
              <div className="p-inputgroup align-items-center">
                <Controller
                  name="case_description.venue"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper
                      type="text"
                      value={field.value}
                      placeholder="Enter New Venue"
                      onChange={(e) => field.onChange(e.target.value.trimStart())}
                      className="input-shadow w-100"
                    />
                  )}
                />
                <Button
                  icon="pi pi-check"
                  className="p-button-secondary mx-2"
                  type="button"
                  onClick={handleAddVenue}
                  loading={isAddVenueLoading}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary outline"
                  type="button"
                  onClick={() => {
                    setToggleVenue(false);
                    setValue(
                      'case_description.venue',
                      selectedVenue ? selectedVenue?.venue_id || selectedVenue : watch('case_description.venue')
                    );
                  }}
                  disabled={isAddVenueLoading}
                />
              </div>
            ) : (
              <>
                {/* <Controller
                  name="case_description.venue"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      name="venue"
                      value={field.value}
                      options={venueOptions}
                      optionLabel="venue_name"
                      optionValue="venue_id"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="Select Venue"
                      className="input-shadow w-100"
                      filter
                    />
                  )}
                /> */}
                <Controller
                  name="case_description.venue"
                  control={control}
                  render={({ field }) => {
                    let selectedOption = venueListOptions?.find((opt) => opt?.venue_id === field?.value) || null;
                    if (newVenueName) {
                      selectedOption = { label: newVenueName, value: watch('case_description.venue') };
                    } else if (!selectedOption && watch('case_description.venue')) {
                      selectedOption = (venueValue && venueId && { label: venueValue, value: venueId }) || null;
                    }
                    return (
                      <AsyncPaginate
                        name="venue"
                        value={selectedOption}
                        onChange={(selectedOption) => {
                          if (newVenueName) {
                            setNewVenueName('');
                          }
                          field.onChange(selectedOption?.venue_id);
                          setSelectedVenue(selectedOption);
                        }}
                        loadOptions={loadVenueOptions}
                        getOptionLabel={(option) => option?.label || option?.venue_name}
                        getOptionValue={(option) => option?.value || option?.venue_id}
                        additional={{
                          pageLimit: 15,
                          page: 0,
                        }}
                        debounceTimeout={300}
                        placeholder="Select Venue"
                        className="input-shadow w-100"
                        styles={userDetails?.firmDetails?.appearance_preference}
                        cacheUniqs={[watch('case_description.case_practice_area')]}
                      />
                    );
                  }}
                />
              </>
            )}
            <div className="col-1 col-md-1 px-1 d-flex align-items-center ms-1 w-auto">
              <span className="pointer">
                <i
                  className="fas fa-plus-circle add-venue"
                  onClick={() => {
                    setToggleVenue(true);
                    setValue('case_description.venue', '');
                  }}
                ></i>
                <Tooltip content="Add Venue" position="top" target=".add-venue" showDelay={500} />
              </span>
            </div>
          </div>
        </div>

        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Date of Loss/Event</div>
          <div className="col-md-8 col-12 edit-field">
            <Controller
              name="case_description.date_of_loss_event"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Calendar
                  monthNavigator
                  yearNavigator
                  yearRange="1900:2100"
                  showButtonBar
                  dateFormat={AppConstants.date_format}
                  value={value}
                  onChange={(e) => onChange(e.value)}
                  className="rounded flex-grow-1 w-100 input-shadow"
                  placeholder="Select"
                />
              )}
            />
          </div>
        </div>

        <div className="row align-items-center py-2 flex-wrap">
          <div className="col-md-4 col-12 text-muted p-medium">Tags</div>
          <div className="col-md-8 col-12 edit-field">
            <Controller
              name="tags"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Chips
                  itemTemplate={chipTemplate}
                  placeholder="Select Tags"
                  value={field.value}
                  onChange={(e) => {
                    let arr = [];
                    if (e?.value?.length) {
                      arr = e.value.map((val) => val.replace(/\s+/g, ''));
                    }
                    field.onChange([...new Set(arr || e.target.value)]);
                  }}
                  className="input-shadow w-100 taging lead-tags"
                />
              )}
            />
          </div>
        </div>

        {userDetails.checkSubPermission('tags') && (
          <div className="row align-items-center py-2 flex-wrap">
            <div className="col-12 text-muted p-medium">
              <Button
                type="button"
                className="p-button button-text text-medium addfield ps-0"
                onClick={(e) => {
                  e.preventDefault();
                  setShowGlobalTags(!showGlobalTags);
                }}
              >
                {<i className={showGlobalTags ? 'pi pi-minus F-size12 me-1' : 'icon-add F-size14 me-1 '} />}Add Global Tags
              </Button>
            </div>
            <div className="col-12 edit-field">
              {showGlobalTags && (
                <>
                  {globalTagsList?.length > 0 ? (
                    <div style={{ position: 'relative' }}>
                      <div
                        className="global-tag-wrapper input-shadow lead-tags"
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          maxHeight: '150px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          padding: '8px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          position: 'relative',
                        }}
                      >
                        {globalTagsList?.map((element, index) => {
                          let hashReplacedTag = element.value.replace('#', '');
                          return (
                            <span
                              key={index}
                              className={`tag-highlight ${watch('tags')?.includes(hashReplacedTag) ? 'selected-global-tag' : ''}`}
                              style={{ height: 'fit-content' }}
                              onClick={(e) => {
                                let existingTags = watch('tags');
                                existingTags?.push(hashReplacedTag);
                                setValue('tags', [...new Set(existingTags)]);
                              }}
                            >
                              {element.value}
                            </span>
                          );
                        })}
                      </div>

                      {globalTagToken && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '10px',
                            right: '10px',
                            borderRadius: '4px',
                            zIndex: 1,
                          }}
                        >
                          <span
                            type="button"
                            className="p-button button-text text-medium ps-0"
                            onClick={async () => {
                              loadMoreGlobalTags();
                            }}
                          >
                            Load More
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="row mb-2">
                      <div className="col-12 small d-flex align-items-center">
                        <i className="fas fa-info-circle me-2"></i>
                        <span className="text-hint ms-1">No Global Tags Found.</span>{' '}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

export default Summary;
